// src/components/lending/ReportCard.js
import React, { useState } from 'react';
import { ReportStatus, BorrowerResponseType } from '../constants';
import { reportService } from '../../services/reportService';
import { fileStorageService } from '../../services/fileStorageService';
import { useAuth } from '../../context/AuthContext';
import { MessageSquare, CheckCircle, Clock, AlertTriangle, User, Calendar, Paperclip, X, FileText, Users, CornerDownRight, Edit2, Loader2, Download } from 'lucide-react';
import { Link } from 'react-router-dom';

const statusConfig = {
  [ReportStatus.PENDING_RESPONSE]: { color: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20', icon: Clock, label: 'Pending Response (Hidden)' },
  [ReportStatus.PUBLISHED_UNRESOLVED]: { color: 'bg-red-500/10 text-red-500 border-red-500/20', icon: AlertTriangle, label: 'Unresolved Report' },
  [ReportStatus.PUBLISHED_DISPUTED]: { color: 'bg-orange-500/10 text-orange-500 border-orange-500/20', icon: MessageSquare, label: 'Active Discussion' },
  [ReportStatus.PUBLISHED_SETTLED]: { color: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20', icon: CheckCircle, label: 'Settled' },
  [ReportStatus.WITHDRAWN]: { color: 'bg-slate-500/10 text-slate-500', icon: User, label: 'Withdrawn' },
};

const responseTypeConfig = {
  [BorrowerResponseType.DISPUTE]: { 
    color: 'border-orange-500', 
    bg: 'bg-orange-500', 
    bgSoft: 'bg-orange-500/10',
    text: 'text-orange-100', 
    title: 'Dispute Filed' 
  },
  [BorrowerResponseType.SETTLEMENT_OFFER]: { 
    color: 'border-blue-500', 
    bg: 'bg-blue-500', 
    bgSoft: 'bg-blue-500/10',
    text: 'text-blue-100', 
    title: 'Settlement Offer' 
  },
  [BorrowerResponseType.CLAIM_PAID]: { 
    color: 'border-emerald-500', 
    bg: 'bg-emerald-500', 
    bgSoft: 'bg-emerald-500/10',
    text: 'text-emerald-100', 
    title: 'Payment Claimed' 
  },
};

const ReportCard = ({ report, isPublic = false, isOwner = false, onUpdate }) => {
  const { currentUser } = useAuth();
  const config = statusConfig[report.status] || statusConfig[ReportStatus.PUBLISHED_UNRESOLVED];
  const Icon = config.icon;
  const [selectedImage, setSelectedImage] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const responseStyle = report.borrowerResponseType ? responseTypeConfig[report.borrowerResponseType] : responseTypeConfig[BorrowerResponseType.DISPUTE];
  const hasMultipleBorrowers = report.additionalBorrowers && report.additionalBorrowers.length > 0;

  // Check if current user is the owner
  const isReportOwner = currentUser && currentUser.uid === report.lenderId;

  // Handle file download/view
  const handleFileView = async (file) => {
    if (file.url) {
      // If it's a Firebase Storage URL, open directly
      window.open(file.url, '_blank');
    } else if (file.data) {
      // If it's base64 data (legacy), open in new tab
      const newWindow = window.open();
      if (file.type === 'application/pdf') {
        newWindow.document.write(`<iframe width='100%' height='100%' src='${file.data}'></iframe>`);
      } else {
        newWindow.document.write(`<img src='${file.data}' style='max-width:100%;height:auto;'/>`);
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      // Handle Firestore Timestamp
      if (dateString.seconds) {
        return new Date(dateString.seconds * 1000).toLocaleDateString();
      }
      // Handle ISO string or regular date
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return dateString;
    }
  };

  return (
    <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden shadow-lg hover:shadow-2xl transition-shadow duration-300">
      {/* Header / Status Bar */}
      <div className={`px-4 py-2 border-b border-slate-700/50 flex justify-between items-center ${config.color} bg-opacity-5`}>
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider">
          <Icon size={14} />
          {config.label}
        </div>
        <div className="text-xs opacity-70">
          Reported: {formatDate(report.createdDate || report.createdAt)}
        </div>
      </div>

      <div className="p-6">
        <div className="flex justify-between items-start mb-6">
          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2 flex-wrap">
              <span className="text-slate-400 font-normal">Report on:</span> 
              {report.borrowerName} 
              {hasMultipleBorrowers && (
                <span className="text-xs text-slate-500 bg-slate-900 px-1.5 py-0.5 rounded border border-slate-700">
                  + {report.additionalBorrowers.length} Other{report.additionalBorrowers.length > 1 ? 's' : ''}
                </span>
              )}
            </h3>
            <p className="text-slate-400 font-mono text-sm mt-1">{report.borrowerMobile}</p>
            <div className="mt-2 text-xs text-slate-500 flex flex-wrap gap-2">
              <span className="bg-slate-900 px-2 py-0.5 rounded border border-slate-700">
                Lender ID: {report.lenderId?.substring(0, 8) || 'Unknown'}...
              </span>
              {report.lenderName && (
                <span className="bg-slate-900 px-2 py-0.5 rounded border border-slate-700">
                  {report.lenderName}
                </span>
              )}
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-slate-400">Claim Amount</p>
            <p className="text-2xl font-mono font-bold text-white">
              R{report.amount?.toLocaleString() || '0'}
            </p>
          </div>
        </div>
        
        {/* List of Borrowers Section */}
        {hasMultipleBorrowers && (
          <div className="mb-6 bg-slate-900/50 p-4 rounded-lg border border-slate-700/50">
            <h4 className="text-xs font-bold text-slate-400 uppercase mb-3 flex items-center gap-2">
              <Users size={14} /> Parties Involved
            </h4>
            <div className="space-y-2">
              {/* Primary */}
              <div className="flex justify-between items-center text-sm border-b border-slate-800 pb-2">
                <div>
                  <span className="text-white font-medium">{report.borrowerName}</span>
                  <span className="ml-2 text-xs bg-emerald-900 text-emerald-400 px-1.5 rounded">Primary</span>
                </div>
                <span className="text-slate-400 font-mono text-xs">{report.borrowerMobile}</span>
              </div>
              {/* Co-Borrowers */}
              {report.additionalBorrowers?.map((cb, idx) => (
                <div key={idx} className="flex justify-between items-center text-sm">
                  <div className="flex items-center gap-2">
                    <CornerDownRight size={12} className="text-slate-600"/>
                    <span className="text-slate-300">{cb.name}</span>
                  </div>
                  <span className="text-slate-500 font-mono text-xs">{cb.mobile}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* The Thread View */}
        <div className="space-y-6">
          {/* Lender's Statement */}
          <div className="relative pl-6 border-l-2 border-slate-600">
            <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-slate-600 border-2 border-slate-800"></div>
            <div className="mb-2 flex items-center gap-2">
              <span className="text-xs font-bold text-slate-300 bg-slate-700 px-2 py-0.5 rounded">Lender Statement</span>
              <span className="text-xs text-slate-500">{report.lenderName}</span>
            </div>
            
            <div className="bg-slate-900/30 rounded p-3 border border-slate-700/30">
              <p className="text-slate-300 text-sm leading-relaxed italic">"{report.description}"</p>
              <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-500 border-t border-slate-700/50 pt-2">
                <span className="flex items-center gap-1">
                  <Calendar size={12}/> Due: {report.dueDate}
                </span>
                {report.evidenceAttachments && report.evidenceAttachments.length > 0 && (
                  <span className="flex items-center gap-1 text-emerald-500 font-medium">
                    <Paperclip size={12}/> {report.evidenceAttachments.length} Attachment(s)
                  </span>
                )}
              </div>
            </div>

            {/* Evidence Gallery */}
            {report.evidenceAttachments && report.evidenceAttachments.length > 0 && (
              <div className="mt-2 flex gap-2 overflow-x-auto py-2">
                {report.evidenceAttachments.map((item, idx) => {
                  if (item.type === 'application/pdf') {
                    return (
                      <button 
                        key={item.id || idx} 
                        onClick={() => handleFileView(item)}
                        className="relative group shrink-0 h-16 w-16 bg-slate-900 flex flex-col items-center justify-center rounded border border-slate-700 hover:border-emerald-500 transition-colors"
                        title="View PDF"
                      >
                        <FileText className="text-red-400" size={24} />
                        <span className="text-[9px] text-slate-500 mt-1 font-bold">PDF</span>
                      </button>
                    );
                  }
                  return (
                    <button 
                      key={item.id || idx} 
                      onClick={() => setSelectedImage(item.url || item.data)}
                      className="relative group shrink-0"
                      title="View Image"
                    >
                      <img 
                        src={item.url || item.data} 
                        alt={item.name} 
                        className="h-16 w-16 object-cover rounded border border-slate-700 group-hover:border-emerald-500 transition-colors" 
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors rounded"></div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Borrower's Response */}
          {report.borrowerResponse && (
            <div className={`relative pl-6 border-l-2 ${responseStyle.color}`}>
              <div className={`absolute -left-[9px] top-0 w-4 h-4 rounded-full ${responseStyle.bg} border-2 border-slate-800`}></div>
              
              <div className={`rounded-lg ${responseStyle.bgSoft} p-4 border border-white/5`}>
                <div className="mb-3 flex justify-between items-start">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded ${responseStyle.bg} ${responseStyle.text}`}>
                      {responseStyle.title}
                    </span>
                    <span className="text-xs text-slate-400">Borrower Response</span>
                  </div>
                  <span className="text-xs text-slate-500">{report.borrowerResponseDate}</span>
                </div>
                
                <p className="text-slate-200 text-sm leading-relaxed whitespace-pre-wrap">"{report.borrowerResponse}"</p>

                {/* Borrower Evidence Gallery */}
                {report.borrowerEvidenceAttachments && report.borrowerEvidenceAttachments.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-white/10">
                    <p className="text-xs font-bold opacity-70 mb-2 flex items-center gap-1 text-slate-400">
                      <Paperclip size={12}/> Supporting Evidence
                    </p>
                    <div className="flex gap-2 overflow-x-auto">
                      {report.borrowerEvidenceAttachments.map((item, idx) => {
                        if (item.type === 'application/pdf') {
                          return (
                            <button 
                              key={item.id || idx} 
                              onClick={() => handleFileView(item)}
                              className="relative group shrink-0 h-16 w-16 bg-slate-900/50 flex flex-col items-center justify-center rounded border border-slate-600 hover:border-white transition-colors"
                              title="View PDF"
                            >
                              <FileText className="text-red-400" size={24} />
                              <span className="text-[9px] text-slate-400 mt-1 font-bold">PDF</span>
                            </button>
                          );
                        }
                        return (
                          <button 
                            key={item.id || idx} 
                            onClick={() => setSelectedImage(item.url || item.data)}
                            className="relative group shrink-0"
                            title="View Image"
                          >
                            <img 
                              src={item.url || item.data} 
                              alt={item.name} 
                              className="h-16 w-16 object-cover rounded border border-slate-600 group-hover:border-white transition-colors" 
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors rounded"></div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Lender Reply */}
          {report.lenderReply && (
            <div className="relative pl-6 border-l-2 border-slate-600">
              <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-slate-600 border-2 border-slate-800"></div>
              <div className="mb-2 flex items-center gap-2">
                <span className="text-xs font-bold text-slate-300 bg-slate-700 px-2 py-0.5 rounded">Lender Reply</span>
                <span className="text-xs text-slate-500">{report.lenderReplyDate}</span>
              </div>
              <div className="bg-slate-900/30 rounded p-3 border border-slate-700/30">
                <p className="text-slate-300 text-sm leading-relaxed italic">"{report.lenderReply}"</p>
              </div>
            </div>
          )}
          
          {/* If no response and it's public */}
          {!report.borrowerResponse && isPublic && report.status !== ReportStatus.PUBLISHED_SETTLED && (
            <div className="ml-6 p-3 bg-slate-900/50 rounded-lg border border-dashed border-slate-700 text-xs text-slate-500 text-center flex flex-col items-center gap-1">
              <Clock size={16} className="text-slate-600"/>
              No response submitted by borrower yet.
            </div>
          )}
        </div>

        {/* Actions Footer */}
        <div className="mt-6 pt-4 border-t border-slate-700 flex justify-between items-center flex-wrap gap-3">
          <div className="text-xs text-slate-500 flex gap-2">
            <span>Ref ID: {report.id?.substring(0, 12)}...</span>
          </div>
          
          <div className="flex gap-3">
            {isReportOwner && report.status === ReportStatus.PENDING_RESPONSE && (
              <Link 
                to={`/list/#/edit/${report.id}`} 
                className="text-sm font-medium text-blue-400 hover:text-blue-300 flex items-center gap-1 transition-colors"
              >
                <Edit2 size={14} /> Edit
              </Link>
            )}
            <Link 
              to={`/manage/${report.id}`} 
              className="text-sm font-medium text-emerald-400 hover:text-emerald-300 hover:underline transition-colors"
            >
              View Full Thread & Actions &rarr;
            </Link>
          </div>
        </div>
      </div>

      {/* Lightbox Modal for Evidence */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm animate-in fade-in" 
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-4xl max-h-screen">
            <button 
              onClick={() => setSelectedImage(null)}
              className="absolute -top-10 right-0 text-white hover:text-slate-300 transition-colors"
            >
              <X size={32} />
            </button>
            <img 
              src={selectedImage} 
              alt="Evidence" 
              className="max-h-[85vh] rounded-lg shadow-2xl border border-slate-800" 
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportCard;